import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AVAILABLE_CREDIT_HELP,
  getNextPaymentDue,
  getWallet,
  downloadStatementPdf,
} from "../../services/portalBillingService";
import { toast } from "react-toastify";

function formatMoney(value) {
  return Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(Number(value || 0));
}

export default function PortalBillingStrip() {
  const [nextDue, setNextDue] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [dueRes, walletRes] = await Promise.all([getNextPaymentDue(), getWallet()]);
        if (!cancelled) {
          setNextDue(dueRes?.data?.data || null);
          setWallet(walletRes?.data?.data || null);
        }
      } catch (_) {
        if (!cancelled) {
          setNextDue(null);
          setWallet(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStatement = async () => {
    try {
      setDownloading(true);
      await downloadStatementPdf({}, "statement-of-account.pdf");
    } catch (ex) {
      toast.error("Failed to download statement of account.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <p className="text-muted small">Loading billing…</p>;
  }

  const inv = nextDue?.invoice;
  const help = wallet?.help || AVAILABLE_CREDIT_HELP;

  return (
    <div className="row mb-3">
      <div className="col-md-4 mb-2">
        <div className="card h-100">
          <div className="card-body">
            <h6 className="card-title text-secondary mb-2">{nextDue?.label || "Next payment due"}</h6>
            {nextDue?.hasDue && inv ? (
              <>
                <div className="h5 mb-1">{formatMoney(inv.balance)}</div>
                <div className="small text-muted mb-2">
                  {inv.obligationLabel || "Invoice"} · {inv.invoicenum || `#${inv.id}`}
                  {inv.duedate ? ` · due ${inv.duedate}` : ""}
                  {nextDue.isOverdue ? " · overdue" : ""}
                </div>
                <Link to={`/invoices/${inv.id}`} className="btn btn-primary btn-sm mr-2">
                  View / Pay
                </Link>
                <Link to="/invoices" className="btn btn-light btn-sm">
                  All invoices
                </Link>
              </>
            ) : (
              <p className="text-muted small mb-0">{nextDue?.message || "No payment due."}</p>
            )}
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-2">
        <div className="card h-100">
          <div className="card-body">
            <h6 className="card-title text-secondary mb-2">
              Available Credit{" "}
              <i
                className="fa fa-info-circle text-muted"
                title={help}
                aria-label={help}
              />
            </h6>
            <div className="h5 mb-1">{formatMoney(wallet?.availableCredit)}</div>
            <p className="small text-muted mb-0">{help}</p>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-2">
        <div className="card h-100">
          <div className="card-body">
            <h6 className="card-title text-secondary mb-2">Statement of Account</h6>
            <p className="small text-muted mb-2">
              Download a PDF of invoices, receipts, and credit for the current period.
            </p>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={downloading}
              onClick={handleStatement}
            >
              <i className="fa fa-file-pdf-o" /> {downloading ? "Preparing…" : "Download SoA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
